import React, { useEffect, useMemo, useState } from 'react';
import scssVariables from '../../../../../Config.module.scss';
import { Currency, DemeterDataFrequency, MarketIndicatorFactorDataModel, UnitOfMeasure } from '../../../../../Generated/Raven-Demeter';
import {
    leadingIndicatorTypeDefinitions,
    SeasonalParameters,
} from '../../../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import { IChartAreaRangeDataSeries, IChartDataSeries } from '../../ChartDefinitions';
import chartService from '../../ChartService';
import ChartWrapper from '../../ChartWrapper/ChartWrapper';
import marketIndicatorChartDateService from '../MarketIndicatorChartDateService';
import { defaultSeasonalParameters } from '../MarketIndicatorChartDefaultParameters';
import marketIndicatorChartService from '../MarketIndicatorChartService';
import {
    defaultChartPalette,
    IMarketIndicatorChartColorPalette,
    IMarketIndicatorUserChartBaseProps,
    ISeasonalSeries,
} from '../MarketIndicatorUserChartDefinitions';
import MarketIndicatorUserChartRaw from '../MarketIndicatorUserChartRaw';
import styles from './MarketIndicatorSeasonalChart.module.scss';
import marketIndicatorSeasonalChartService from './MarketIndicatorSeasonalChartService';

export const seasonalChartPalette: IMarketIndicatorChartColorPalette = {
    dataDecileAlphaColorsRuleSetTwo: [scssVariables.ruleSetTwoAreaColor1],
    lineChartColorsRuleSetTwo: defaultChartPalette.lineChartColorsRuleSetTwo,
};

export interface IMarketIndicatorSeasonalChartProps extends IMarketIndicatorUserChartBaseProps {
    parameters?: SeasonalParameters;
}

const MarketIndicatorSeasonalChart: React.FC<IMarketIndicatorSeasonalChartProps> = (props: IMarketIndicatorSeasonalChartProps) => {
    const [translations, translate] = useLanguage();
    const [lineSeries, setLineSeries] = useState<IChartDataSeries[]>([]);
    const [areaRangeSeries, setAreaRangeSeries] = useState<IChartAreaRangeDataSeries[]>([]);

    const dataFrequency = useMemo(
        () => props.runTestMarketIndicatorFactorResponse?.dataFrequency ?? DemeterDataFrequency.Weekly,
        [props.runTestMarketIndicatorFactorResponse],
    );

    const numberOfYears = useMemo(() => {
        const parameters = props.parameters ?? defaultSeasonalParameters;
        const value = Number(parameters.yearsOfData);
        return value;
    }, [props.parameters]);

    const chartData = useMemo(() => {
        if (
            props.runTestMarketIndicatorFactorResponse &&
            props.runTestMarketIndicatorFactorResponse.rows &&
            props.runTestMarketIndicatorFactorResponse.rows.length > 0
        ) {
            return marketIndicatorChartService.convertToChartData((props.runTestMarketIndicatorFactorResponse?.rows as MarketIndicatorFactorDataModel[]) ?? []);
        }
        return [];
    }, [props.runTestMarketIndicatorFactorResponse]);

    const isLoading = useMemo(
        () => props.isLoading || !props.runTestMarketIndicatorFactorResponse,
        [props.runTestMarketIndicatorFactorResponse, props.isLoading],
    );

    const title = useMemo(() => {
        if (props.title) {
            return props.title;
        }

        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.commodityDisplayName) {
            const region = props.runTestMarketIndicatorFactorResponse?.subRegion
                ? translate(props.runTestMarketIndicatorFactorResponse?.subRegion)
                : translate(props.runTestMarketIndicatorFactorResponse?.regionDisplayName!);
            const tableDefinitionType =
                translations.tableDefinitionType[
                    leadingIndicatorTypeDefinitions.find((x) => x.value === props.runTestMarketIndicatorFactorResponse?.leadingIndicatorType)
                        ?.tableDefinitionType!
                ];
            const commdityName = props.runTestMarketIndicatorFactorResponse.commodityDisplayName;
            return [region, commdityName, tableDefinitionType].filter((x) => x !== null && x !== '').join(' ');
        }
        return '';
    }, [props.runTestMarketIndicatorFactorResponse]);

    const sourceTag = useMemo(() => {
        if (
            props.runTestMarketIndicatorFactorResponse &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags &&
            props.runTestMarketIndicatorFactorResponse.dataSourceTags.length > 0
        ) {
            return `${props.runTestMarketIndicatorFactorResponse.dataSourceTags[0]}, ${translations.dataSource.StoneXCalculations}`;
        }
        return '';
    }, [props.runTestMarketIndicatorFactorResponse]);

    const currency = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.currency) {
            return props.runTestMarketIndicatorFactorResponse.currency;
        }
        return '' as Currency;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const unitOfMeasure = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.unitOfMeasure) {
            return props.runTestMarketIndicatorFactorResponse.unitOfMeasure;
        }
        return '' as UnitOfMeasure;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const displayUnitOfMeasure = useMemo(() => {
        if (props.runTestMarketIndicatorFactorResponse && props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits) {
            return props.runTestMarketIndicatorFactorResponse.dataCombinationDisplayUnits;
        }

        return '' as UnitOfMeasure;
    }, [props.runTestMarketIndicatorFactorResponse]);

    const averageChange = useMemo(() => {
        if (chartData.length > 0) {
            const value = marketIndicatorSeasonalChartService.calculateSeasonalAverageChange(chartData, numberOfYears, dataFrequency);

            return formattingService.formatNumber(value, {
                automaticDecimals: true,
                zeroPlaceholder: true,
            });
        }

        return formattingService.formatNumber(0, {
            automaticDecimals: true,
            zeroPlaceholder: true,
        });
    }, [props.runTestMarketIndicatorFactorResponse]);

    const lastChange = useMemo(() => {
        if (chartData.length > 0) {
            const value = marketIndicatorSeasonalChartService.calculateLastChange(chartData, dataFrequency);
            return formattingService.formatNumber(value, {
                automaticDecimals: true,
                zeroPlaceholder: true,
            });
        }

        return formattingService.formatNumber(0, {
            automaticDecimals: true,
            zeroPlaceholder: true,
        });
    }, [props.runTestMarketIndicatorFactorResponse]);

    const hasAverageChangeFooter = useMemo(() => {
        if (!props.parameters?.subFactorCalculationTypes) {
            return false;
        }

        return props.parameters?.subFactorCalculationTypes.indexOf('VersesAverageChange') >= 0;
    }, [props.parameters?.subFactorCalculationTypes]);

    const chartLinesAndAreaSeriesData = useMemo<ISeasonalSeries | null>(() => {
        if (
            !props.runTestMarketIndicatorFactorResponse ||
            !props.runTestMarketIndicatorFactorResponse?.rows ||
            !props.runTestMarketIndicatorFactorResponse.rows.length ||
            !props.parameters
        ) {
            return null;
        }

        const dataForSeries = marketIndicatorChartService.getSeasonalChartSeriesData(
            props.runTestMarketIndicatorFactorResponse.rows,
            dataFrequency,
            numberOfYears,
            props.parameters?.startMonth,
            props.parameters?.endMonth,
        );
        return dataForSeries;
    }, [props.runTestMarketIndicatorFactorResponse, props.parameters]);

    useEffect(() => {
        if (!chartLinesAndAreaSeriesData) {
            return;
        }

        const startMonth = props.parameters?.startMonth ?? defaultSeasonalParameters.startMonth;
        const endMonth = props.parameters?.endMonth ?? defaultSeasonalParameters.endMonth;
        const minimumChartDisplayDate = new Date(new Date().setMonth(+startMonth - 1));
        const maximumChartDisplayDate = new Date(new Date().setMonth(+endMonth - 1));
        minimumChartDisplayDate.setDate(1);
        const lastMonthOfCalculationData =
            (chartLinesAndAreaSeriesData.thisYearLineSeries[chartLinesAndAreaSeriesData.thisYearLineSeries.length - 1]?.asOfDate.getMonth() ?? -1) + 1;
        const startMonthIsAfterCurrentData = lastMonthOfCalculationData < +startMonth;
        const lastYearOnly = +startMonth < +endMonth && startMonthIsAfterCurrentData;
        if (lastYearOnly) {
            minimumChartDisplayDate.setFullYear(minimumChartDisplayDate.getFullYear() - 1);
            maximumChartDisplayDate.setFullYear(maximumChartDisplayDate.getFullYear() - 1);
        }

        let thisYearLineSeries;
        let lastYearLineData;
        let yearsAverageLineSeries;
        let areaYearsRangeSeries;
        if (dataFrequency !== DemeterDataFrequency.Monthly) {
            thisYearLineSeries = marketIndicatorChartDateService.toWeeklySeriesData(
                chartLinesAndAreaSeriesData.thisYearLineSeries,
                minimumChartDisplayDate,
                true,
            );
            lastYearLineData = marketIndicatorChartDateService.toWeeklySeriesData(chartLinesAndAreaSeriesData.lastYearLineSeries, minimumChartDisplayDate);
            yearsAverageLineSeries = marketIndicatorChartDateService.toWeeklySeriesData(
                chartLinesAndAreaSeriesData.nYearAverageLineSeries,
                minimumChartDisplayDate,
            );
            areaYearsRangeSeries = marketIndicatorChartDateService.toWeeklySeriesData(chartLinesAndAreaSeriesData.areaRangeSeries, minimumChartDisplayDate);
        } else {
            thisYearLineSeries = chartLinesAndAreaSeriesData.thisYearLineSeries;
            lastYearLineData = chartLinesAndAreaSeriesData.lastYearLineSeries;
            yearsAverageLineSeries = chartLinesAndAreaSeriesData.nYearAverageLineSeries;
            areaYearsRangeSeries = chartLinesAndAreaSeriesData.areaRangeSeries;
        }

        const newLinesSeries: IChartDataSeries[] = [
            {
                label: translations.text.thisYear,
                data: thisYearLineSeries,
                axisLabel: props.yAxisLabel,
            },
            {
                label: translations.text.lastYear,
                data: lastYearLineData,
                axisLabel: props.yAxisLabel,
            },
            {
                label: `${numberOfYears} ${translations.text.yearlyAverage}`,
                data: yearsAverageLineSeries,
                axisLabel: props.yAxisLabel,
            },
        ];

        const newAreaSeries = [
            {
                label: `${numberOfYears} ${translations.text.yearRange}`,
                data: areaYearsRangeSeries,
            } as IChartAreaRangeDataSeries,
        ];

        setLineSeries(newLinesSeries);
        setAreaRangeSeries(newAreaSeries);
    }, [chartLinesAndAreaSeriesData]);

    const footer = hasAverageChangeFooter && (
        <div className={styles.market_indicator_seasonal_chart_footer}>
            <span className={styles.market_indicator_seasonal_chart_footer_item}>
                {translations.text.averageChange}: {averageChange}
            </span>
            <span className={styles.market_indicator_seasonal_chart_footer_item}>
                {translations.text.lastChange}: {lastChange}
            </span>
        </div>
    );
    const minimumChartDate = lineSeries && lineSeries[1] ? lineSeries[1].data[0]?.asOfDate : new Date();
    const maximumChartDate = lineSeries && lineSeries[1] ? lineSeries[1].data[lineSeries[1].data.length - 1]?.asOfDate : new Date();

    return (
        <ChartWrapper
            name="MarketIndicatorSeasonalChart"
            title={title}
            customTitle={props.customTitle}
            marketIndicatorOutlook={props.marketIndicatorOutlook}
            dataSourceTag={sourceTag}
            isLoading={isLoading}
            headerOptions={{
                showOnlyAsPopout: props.showOnlyAsPopout,
            }}
            showPopout={props.showPopout}
            setShowPopout={props.setShowPopout}
            footer={footer}
            testId="MarketIndicatorFactorChart"
            track={{ eventCategory: 'MarketIndicators', eventAction: 'SeasonalChart' }}
        >
            <MarketIndicatorUserChartRaw
                lineSeries={lineSeries}
                areaRangeSeries={areaRangeSeries}
                currencies={[currency]}
                unitOfMeasures={[unitOfMeasure]}
                displayUnitOfMeasure={displayUnitOfMeasure}
                colorPalette={seasonalChartPalette}
                startOnTick
                focusOnLine
                minimumAxisDate={minimumChartDate?.getTime()}
                maximumAxisDate={maximumChartDate?.getTime()}
                disableTooltip={chartService.isRestrictedDataSource(...(props.runTestMarketIndicatorFactorResponse?.dataSourceTags ?? []))}
            />
        </ChartWrapper>
    );
};

export default MarketIndicatorSeasonalChart;
